import * as React from 'react';
import styled from 'styled-components';
import { forDesktop, forTablet, forIE } from '@westwing/ui-kit/breakPoints';
import { plainTextStyle } from '@westwing/ui-kit/typography';
import { InlineSpinner } from '@westwing/ui-kit/Spinner';
import { useLooksTracking } from '../hooks/useLooksTracking';
import LooksTile from './LooksTile';
import PromotionalElement from './PromotionalElement';
import getGridTiles, { LLPGridTile } from './getGridTiles';
import useBellaTranslator from 'Client/hooks/useTranslator';
import HybridLink from 'Client/components/Generic/Links/HybridLink';
import { TrackLLPImpressionEvents, TrackLooksActions } from 'Common/ga/types';
import useRouter from 'Client/hooks/useRouter';
import { useBellaDispatch, useBellaSelector } from 'Client/redux/hooks';
import { resetLooksListingAction } from 'Client/redux/looksPage/actions';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';

const StyledLooksGridWrapper = styled.div`
    position: relative;
`;

const StyledGrid = styled.div`
    display: grid;
    grid-gap: 0 10px;

    ${forTablet`
        grid-template-columns: repeat(auto-fill, minmax(30%, 1fr));
    `}

    ${forDesktop`
        grid-template-columns: repeat(auto-fill, minmax(24%, 1fr));
    `}

    ${forIE`
        display: flex;
        flex-flow: row wrap;
        justify-content: space-around;
    `}
`;

const StyledLooksSpinner = styled(InlineSpinner)`
    position: inherit;
`;

const StyledLooksButtonWrapper = styled.div`
    position: absolute;
    height: 84px;
    width: 100%;

    display: flex;
    justify-content: center;

    ${forDesktop`
         background-image: linear-gradient(to bottom, rgb(0 0 0 / 20%), rgba(0, 0, 0, 0));
    `}
`;

const StyledNewLooksButton = styled(HybridLink)`
    ${plainTextStyle}
    z-index: 2;
    line-height: 1;
    letter-spacing: 0.35px;
    color: ${({ theme }) => theme.colors.charcoal100};
    text-decoration: none;

    height: 40px;
    min-width: 96px;
    margin-top: 20px;
    padding: 13px 15px;
    background-color: white;
    border-radius: 20px;
    text-align: center;
`;

const LooksGrid = () => {
    const t = useBellaTranslator();
    const { LLP: LLPTracking } = useLooksTracking();
    const {
        location: { pathname },
    } = useRouter();
    const { p100PromotionalElementsLlp } = useFeatureToggle();
    const dispatch = useBellaDispatch();
    const orderedListing = useBellaSelector(state => state.looksPage.looks?.orderedListing) || {};
    const looks = useBellaSelector(state => state.looksPage.looks.listing || []);
    const totalLooks = useBellaSelector(state => state.looksPage.looks.total);
    const looksPerPage = useBellaSelector(state => state.looksPage.looks.perPage);
    const isFetching = useBellaSelector(state => state.looksPage.isFetching);
    const looksPromotionalElements = useBellaSelector(state => state.looksPromotionalElements.promotions);

    const lastPageWithData: number = Number(Object.keys(orderedListing).pop()) || 1;
    const nextPageDownward = lastPageWithData + 1;
    const hasMoreLooks = looksPerPage ? totalLooks > lastPageWithData * looksPerPage : false;
    const showNewLooksButton = orderedListing[1] === undefined && lastPageWithData > 1;

    const listingTiles = React.useMemo<LLPGridTile[]>(() => {
        if (p100PromotionalElementsLlp) {
            return getGridTiles(looks, looksPromotionalElements);
        }
        return getGridTiles(looks, []);
    }, [looks, p100PromotionalElementsLlp, looksPromotionalElements]);

    const handleNewLooksButtonClick = () => {
        dispatch(resetLooksListingAction());
        LLPTracking.track(TrackLooksActions.LLPSeeAllLooksButtonClick, pathname);
    };

    React.useEffect(() => {
        if (showNewLooksButton) {
            LLPTracking.trackImpression(
                TrackLLPImpressionEvents.SeeAllLooksButtonImpression,
                TrackLooksActions.LLPSeeAllLooksButtonImpression,
                pathname
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <StyledLooksGridWrapper>
            {showNewLooksButton && (
                <StyledLooksButtonWrapper>
                    <StyledNewLooksButton to="/looks/" testId="see-all-looks" onClick={handleNewLooksButtonClick}>
                        {t('See all Looks')}
                    </StyledNewLooksButton>
                </StyledLooksButtonWrapper>
            )}
            <StyledGrid>
                {listingTiles.map((tile, index) =>
                    tile.type === 'look' ? (
                        <LooksTile
                            nextPageDownward={nextPageDownward}
                            key={tile.data.slug}
                            index={index}
                            look={tile.data}
                            addDownWardRef={listingTiles.length === index + 1 && hasMoreLooks}
                        />
                    ) : (
                        <PromotionalElement {...tile.data} />
                    )
                )}
            </StyledGrid>
            {isFetching ? <StyledLooksSpinner data-testid="looks-listing-spinner" /> : ''}
        </StyledLooksGridWrapper>
    );
};

export default LooksGrid;
